import type {
  GanttV5Group,
  GanttV5Model,
  GanttV5ModelDiagnostic,
  GanttV5Row,
  GanttV5Task,
  GanttV5VisibleRows,
} from './types';

const ROOT_KEY = '__gantt_v5_root__';

const appendUnique = <T extends { id: string }>(
  values: T[],
  code: GanttV5ModelDiagnostic['code'],
  diagnostics: GanttV5ModelDiagnostic[],
): Map<string, T> => {
  const result = new Map<string, T>();
  for (const value of values) {
    if (result.has(value.id)) {
      diagnostics.push({
        code,
        id: value.id,
        message: `检测到重复标识 ${value.id}，已保留首个定义。`,
      });
      continue;
    }
    result.set(value.id, value);
  }
  return result;
};

export interface BuildGanttV5ModelInput {
  tasks: GanttV5Task[];
  groups: GanttV5Group[];
  collapsedGroupIds?: ReadonlySet<string>;
  hideEmptyRows?: boolean;
}

export function buildGanttV5Model({
  tasks,
  groups,
  collapsedGroupIds = new Set<string>(),
  hideEmptyRows = false,
}: BuildGanttV5ModelInput): GanttV5Model {
  const diagnostics: GanttV5ModelDiagnostic[] = [];
  const groupById = appendUnique(groups, 'DUPLICATE_GROUP', diagnostics);
  const taskById = appendUnique(tasks, 'DUPLICATE_TASK', diagnostics);
  const childGroupsByParentId = new Map<string, GanttV5Group[]>();
  const tasksByGroupId = new Map<string, GanttV5Task[]>();
  const ungroupedTasks: GanttV5Task[] = [];

  const appendChildGroup = (parentId: string, group: GanttV5Group) => {
    const children = childGroupsByParentId.get(parentId) ?? [];
    children.push(group);
    childGroupsByParentId.set(parentId, children);
  };

  for (const group of Array.from(groupById.values())) {
    if (group.parentId && !groupById.has(group.parentId)) {
      diagnostics.push({
        code: 'ORPHAN_GROUP',
        id: group.id,
        message: `分组 ${group.id} 的父分组 ${group.parentId} 不存在，已作为根分组显示。`,
      });
      appendChildGroup(ROOT_KEY, group);
      continue;
    }
    appendChildGroup(group.parentId ?? ROOT_KEY, group);
  }

  for (const task of Array.from(taskById.values())) {
    if (!task.groupId) {
      ungroupedTasks.push(task);
      continue;
    }
    if (!groupById.has(task.groupId)) {
      diagnostics.push({
        code: 'ORPHAN_TASK',
        id: task.id,
        message: `任务 ${task.id} 的分组 ${task.groupId} 不存在，已作为未分组任务显示。`,
      });
      ungroupedTasks.push(task);
      continue;
    }
    const groupTasks = tasksByGroupId.get(task.groupId) ?? [];
    groupTasks.push(task);
    tasksByGroupId.set(task.groupId, groupTasks);
  }

  const descendantTaskCountByGroupId = new Map<string, number>();
  const descendantTaskIdsByGroupId = new Map<string, string[]>();
  const countActive = new Set<string>();
  const countDescendantTasks = (groupId: string): number => {
    const memoized = descendantTaskCountByGroupId.get(groupId);
    if (memoized !== undefined) return memoized;
    if (countActive.has(groupId)) return 0;
    countActive.add(groupId);
    let count = (tasksByGroupId.get(groupId) ?? []).length;
    for (const child of childGroupsByParentId.get(groupId) ?? []) {
      count += countDescendantTasks(child.id);
    }
    countActive.delete(groupId);
    descendantTaskCountByGroupId.set(groupId, count);
    return count;
  };

  const collectActive = new Set<string>();
  const collectDescendantTaskIds = (groupId: string): string[] => {
    const memoized = descendantTaskIdsByGroupId.get(groupId);
    if (memoized) return memoized;
    if (collectActive.has(groupId)) return [];
    collectActive.add(groupId);
    const ids = (tasksByGroupId.get(groupId) ?? []).map((task) => task.id);
    for (const child of childGroupsByParentId.get(groupId) ?? []) {
      ids.push(...collectDescendantTaskIds(child.id));
    }
    collectActive.delete(groupId);
    descendantTaskIdsByGroupId.set(groupId, ids);
    return ids;
  };

  const rows: GanttV5Row[] = [];
  const groupRowById = new Map<string, number>();
  const taskRowById = new Map<string, number>();
  const visiting = new Set<string>();
  const visited = new Set<string>();

  const markHiddenDescendantsVisited = (groupId: string) => {
    for (const child of childGroupsByParentId.get(groupId) ?? []) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      markHiddenDescendantsVisited(child.id);
    }
  };

  const appendGroup = (group: GanttV5Group, depth: number, parentId?: string) => {
    if (visiting.has(group.id)) {
      diagnostics.push({
        code: 'GROUP_CYCLE',
        id: group.id,
        message: `分组 ${group.id} 存在循环父子关系，循环分支已停止展开。`,
      });
      return;
    }
    if (visited.has(group.id)) return;

    if (hideEmptyRows && countDescendantTasks(group.id) === 0) {
      visited.add(group.id);
      markHiddenDescendantsVisited(group.id);
      return;
    }

    visiting.add(group.id);
    visited.add(group.id);
    const directTasks = tasksByGroupId.get(group.id) ?? [];
    const inlineTasks = directTasks.filter((task) => task.renderOnGroupRow);
    const rowTasks = directTasks.filter((task) => !task.renderOnGroupRow);
    const childGroups = childGroupsByParentId.get(group.id) ?? [];
    const collapsed = collapsedGroupIds.has(group.id);
    const groupRowIndex = rows.length;

    rows.push({
      id: group.id,
      type: 'group',
      label: group.label,
      depth,
      parentId,
      groupId: group.id,
      expanded: !collapsed,
      hasChildren: childGroups.length > 0 || rowTasks.length > 0,
      descendantTaskCount: countDescendantTasks(group.id),
      inlineTaskIds: inlineTasks.map((task) => task.id),
      color: group.color,
      groupType: group.type,
      isSubRow: group.isSubRow,
      showSummaryBar: group.showSummaryBar !== false,
    });
    groupRowById.set(group.id, groupRowIndex);
    for (const task of inlineTasks) taskRowById.set(task.id, groupRowIndex);

    if (!collapsed) {
      for (const child of childGroups) appendGroup(child, depth + 1, group.id);
      for (const task of rowTasks) {
        const rowIndex = rows.length;
        rows.push({
          id: task.id,
          type: 'task',
          label: task.label,
          depth: depth + 1,
          parentId: group.id,
          groupId: group.id,
          taskId: task.id,
          expanded: false,
          hasChildren: false,
          descendantTaskCount: 1,
          inlineTaskIds: [],
          color: task.color,
        });
        taskRowById.set(task.id, rowIndex);
      }
    } else {
      markHiddenDescendantsVisited(group.id);
    }
    visiting.delete(group.id);
  };

  for (const rootGroup of childGroupsByParentId.get(ROOT_KEY) ?? []) {
    appendGroup(rootGroup, 0);
  }

  for (const group of Array.from(groupById.values())) {
    if (!visited.has(group.id)) appendGroup(group, 0);
  }

  for (const task of ungroupedTasks) {
    const rowIndex = rows.length;
    rows.push({
      id: task.id,
      type: 'task',
      label: task.label,
      depth: 0,
      taskId: task.id,
      expanded: false,
      hasChildren: false,
      descendantTaskCount: 1,
      inlineTaskIds: [],
      color: task.color,
    });
    taskRowById.set(task.id, rowIndex);
  }

  for (const groupId of Array.from(groupById.keys())) collectDescendantTaskIds(groupId);

  const taskIdsByRow = new Map<number, string[]>();
  for (const [taskId, rowIndex] of Array.from(taskRowById.entries())) {
    const rowTaskIds = taskIdsByRow.get(rowIndex) ?? [];
    rowTaskIds.push(taskId);
    taskIdsByRow.set(rowIndex, rowTaskIds);
  }

  return {
    rows,
    groupById,
    taskById,
    groupRowById,
    taskRowById,
    taskIdsByRow,
    descendantTaskIdsByGroupId,
    descendantTaskCountByGroupId,
    diagnostics,
  };
}

export interface GetGanttV5VisibleRowsInput {
  rows: GanttV5Row[];
  scrollTop: number;
  viewportHeight: number;
  rowHeight: number;
  overscan?: number;
}

export function getGanttV5VisibleRows({
  rows,
  scrollTop,
  viewportHeight,
  rowHeight,
  overscan = 4,
}: GetGanttV5VisibleRowsInput): GanttV5VisibleRows {
  if (rows.length === 0 || rowHeight <= 0 || viewportHeight <= 0) {
    return {
      startIndex: 0,
      endIndex: 0,
      rows: [],
      offsetTop: 0,
      totalHeight: Math.max(0, rows.length * rowHeight),
    };
  }

  const safeScrollTop = Math.max(0, scrollTop);
  const startIndex = Math.max(0, Math.floor(safeScrollTop / rowHeight) - overscan);
  const endIndex = Math.min(
    rows.length,
    Math.ceil((safeScrollTop + viewportHeight) / rowHeight) + overscan,
  );

  return {
    startIndex,
    endIndex,
    rows: rows.slice(startIndex, endIndex),
    offsetTop: startIndex * rowHeight,
    totalHeight: rows.length * rowHeight,
  };
}
